import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';

/**
 * Asks before a role is taken away.
 *
 * ⚠️ **A dialog rather than a hand-rolled overlay**, and that is not a style preference. The first
 * version of this screen drew its own fixed-position div: no focus trap, no Escape, no return of
 * focus to the button that opened it, and nothing telling a screen reader that the page behind is
 * inert. A control room is operated quickly and sometimes by keyboard. `dialog.tsx` is already
 * vendored here and four other screens use it.
 *
 * ⚠️ **The question names the person AND the role.** "Are you sure?" is a question nobody reads, and
 * the overlay hides the row that would otherwise say who this is. Withdrawing control from the wrong
 * colleague in the middle of a search is the mistake this exists to stop.
 */
export function RemoveRoleDialog({
  personName,
  roleLabel,
  open,
  onOpenChange,
  onConfirm,
}: {
  personName: string;
  roleLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  const { t } = useLocale();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t.users.cancel}>
        <DialogHeader>
          <DialogTitle>{t.users.confirmRemoveTitle}</DialogTitle>
          <DialogDescription>{t.users.confirmRemoveLede}</DialogDescription>
        </DialogHeader>

        <p className="font-semibold">
          {t.users.confirmRemove.replace('{role}', roleLabel).replace('{name}', personName)}
        </p>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t.users.cancel}
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            {t.users.confirmRemoveYes}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
